import React, { ReactElement } from 'react';

import clsx from 'classnames';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';

import { Tooltip } from '../../Tooltip';
import { Box } from '../../Box';
import { useIsDesktop } from '../../hooks';

import useStyles from './Day.styles';

export interface DayProps extends Omit<PickersDayProps<Date>, 'onMouseLeave'> {
  className?: string;
  hidden?: boolean;

  testHoliday?: (day: Date) => boolean;
  testIsInRange?: (day: Date) => boolean;
  testIsStart?: (day: Date) => boolean;
  testIsEnd?: (day: Date) => boolean;

  onMouseLeave?: (
    event: React.MouseEvent<HTMLButtonElement>,
    day?: Date,
  ) => void;

  renderDayTooltip?: (day: Date | null) => ReactElement | null;
  renderDayFooter?: (
    day: Date | null,
    isInRange: boolean,
  ) => ReactElement | null;
}

/**
 * Day - компонент для отображения дня в календаре.
 *
 * @param {DayProps} props - Свойства компонента.
 * @param {Date} props.day - Дата тображаемого дня.
 * @param {string} [props.className] - Дополнительный CSS-класс.
 * @param {boolean} [props.today=false] - Является ли день сегодняшним.
 * @param {boolean} [props.disabled=false] - Отключён ли день.
 * @param {boolean} [props.hidden=false] - Скрыт ли день.
 * @param {boolean} [props.selected=false] - Выбран ли день.
 * @param {boolean} [props.testIsEnd=false] - Функция проверки, является ли день концом диапазона.
 * @param {boolean} [props.testIsStart=false] - Функция проверки, является ли день началом диапазона.
 * @param {boolean} [props.testHoliday=false] - Функция проверки, является ли день праздничным.
 * @param {boolean} [props.testIsInRange=false] - Функция проверки, является ли день частью выбранного диапазона.
 * @param {(day: Date | null) => ReactElement | null} [props.renderDayTooltip] - Функция, которая рендерит тултип.
 * @param {(day: Date | null, isInRange: boolean) => ReactElement | null} [props.renderDayFooter] - Функция, которая рендерит что-либо под цифрой даты.
 * @returns {JSX.Element} Возвращает JSX-элемент представляющий компонент Day.
 */

export const Day: React.FC<DayProps> = ({
  day,
  today,
  disabled,
  hidden,
  selected,
  testIsInRange,
  testIsEnd,
  testIsStart,
  testHoliday,
  className,
  onMouseLeave,
  renderDayTooltip,
  renderDayFooter,
  ...other
}) => {
  const isDesktop = useIsDesktop();

  const { classes } = useStyles();

  const tooltipContent = renderDayTooltip?.(day);

  const isInRange = testIsInRange?.(day);
  const isStart = isInRange && testIsStart?.(day);
  const isEnd = isInRange && testIsEnd?.(day);

  return (
    <Tooltip
      title={tooltipContent || ''}
      placement="bottom"
      disabled={!isDesktop || !tooltipContent}
    >
      <div
        className={clsx(
          classes.dayWrapper,
          isInRange && classes.dayWrapperInRange,
          isStart && classes.dayWrapperStart,
          isEnd && classes.dayWrapperEnd,
        )}
      >
        <Box display="flex" flexDirection="column">
          <PickersDay
            classes={{
              root: classes.day,
              today: classes.current,
              selected: classes.daySelected,
              disabled: classes.dayDisabled,
            }}
            className={clsx(className, {
              [classes.hidden]: hidden,
              [classes.holiday]: testHoliday?.(day),
              aqa_icon_button: true,
            })}
            day={day}
            today={today}
            selected={selected}
            disabled={disabled}
            hidden={hidden}
            tabIndex={hidden || disabled ? -1 : 0}
            disableRipple
            onMouseLeave={(e) => onMouseLeave?.(e, day)}
            {...other}
          />
          {renderDayFooter?.(day, !!isInRange)}
        </Box>
      </div>
    </Tooltip>
  );
};
